import React, { useState, useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { taskTransferApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';

// Moves Task Log rows from this environment (prod) into Pre-Prod, where CI/CD work is managed.
// The checklist is the confirmation: every eligible task starts ticked and only the ticked IDs go.
// Transferred rows are deleted from this log once their Pre-Prod copy is confirmed. The popup
// closes when the transfer returns and the outcome is reported as a notification.
const TaskTransferModal = ({ onClose, onTransferred }) => {
  const { notify } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [sameDatabase, setSameDatabase] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    taskTransferApi.getCandidates()
      .then(res => {
        const rows = res.data?.candidates || [];
        setCandidates(rows);
        setSameDatabase(!!res.data?.sameDatabase);
        setSelected(new Set(rows.map(r => r.id)));
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load tasks'))
      .finally(() => setLoading(false));
  }, []);

  const allTicked = candidates.length > 0 && selected.size === candidates.length;
  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(allTicked ? new Set() : new Set(candidates.map(r => r.id)));

  const unmatched = useMemo(
    () => candidates.filter(r => selected.has(r.id) && r.originatorEmail && !r.originatorMatched).length,
    [candidates, selected]
  );

  const canTransfer = !loading && !executing && !sameDatabase && selected.size > 0;

  const handleTransfer = async () => {
    setExecuting(true);
    setError('');
    try {
      const { transferred, skipped, failed, deleteError } = (await taskTransferApi.execute([...selected])).data;
      const moved = transferred.length + skipped.length;
      if (moved > 0) notify.success(`${moved} task${moved === 1 ? '' : 's'} transferred to Pre-Prod`);
      failed.forEach(f => notify.error(`Prod #${f.prodId} not transferred: ${f.error}`));
      if (deleteError) notify.error(deleteError);
      onTransferred?.();
      onClose();
    } catch (err) {
      // The request itself failed, so nothing moved - keep the popup open with the reason.
      setError(err.response?.data?.error || 'Transfer failed');
      setExecuting(false);
    }
  };

  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-lg">
        <div className="stui-modal-header">
          <h3>Transfer to Pre-Prod</h3>
          <button className="stui-modal-close" onClick={onClose} disabled={executing}><X size={20} /></button>
        </div>

          <div className="stui-modal-body stui-modal-body-grid">
            {loading ? (
              <div className="stui-text-empty">Loading...</div>
            ) : candidates.length === 0 ? (
              <div className="stui-text-empty">No tasks to transfer.</div>
            ) : (
              <div className="stui-data-grid-container">
                <div className="stui-data-grid-scroll-area">
                  <table className="stui-data-grid stui-data-grid-auto">
                    <thead>
                      <tr>
                        <th className="stui-td-center">
                          <input type="checkbox" className="stui-checkbox-input" checked={allTicked} onChange={toggleAll} disabled={executing} title="Tick or clear all" />
                        </th>
                        <th>ID</th>
                        <th>Task</th>
                        <th>Type</th>
                        <th>Originator</th>
                        <th>Pre-Prod</th>
                      </tr>
                    </thead>
                    <tbody>
                      {candidates.map(r => (
                        <tr key={r.id}>
                          <td className="stui-td-center">
                            <input type="checkbox" className="stui-checkbox-input" checked={selected.has(r.id)} onChange={() => toggle(r.id)} disabled={executing} />
                          </td>
                          <td className="stui-text-nowrap">{r.id}</td>
                          <td>{r.task}</td>
                          <td className="stui-text-nowrap">{r.entryType}</td>
                          <td className="stui-text-nowrap" title={r.originatorEmail || undefined}>{r.originatorName || '—'}</td>
                          <td className="stui-text-nowrap">
                            {r.alreadyShipped ? (
                              <span className="stui-badge-gray" title="Already in Pre-Prod - transferring only removes it here">Already shipped</span>
                            ) : !r.originatorEmail ? (
                              <span className="stui-td-dim">No originator</span>
                            ) : r.originatorMatched ? (
                              <span className="stui-badge-green" title="Originator has a Pre-Prod account">User found</span>
                            ) : (
                              <span className="stui-badge-amber" title="No Pre-Prod account - the originator's name and email go at the top of Details">Name in details</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {sameDatabase && (
              <div className="stui-text-error">This environment's task log is the Pre-Prod log, so there is nowhere to transfer to. Transfer runs from Prod.</div>
            )}
            {error && <div className="stui-text-error">{error}</div>}
          </div>

        <div className="stui-modal-footer stui-modal-footer-split">
          <span className="stui-text-sm stui-td-dim">
            {`${selected.size} of ${candidates.length} selected${unmatched ? ` · ${unmatched} originator${unmatched === 1 ? '' : 's'} carried in Details` : ''} · transferred tasks are removed from this log`}
          </span>
          <div className="stui-row-8">
            <button className="stui-btn stui-btn-secondary" onClick={onClose} disabled={executing}>Cancel</button>
            <button className="stui-btn stui-btn-danger" onClick={handleTransfer} disabled={!canTransfer}>
              {executing ? 'Transferring...' : `Transfer ${selected.size}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskTransferModal;
